import { useState } from 'react';
import { Link } from 'react-router-dom';
import { partner } from '../../api/partner';
import type { TripRow } from '../../api/types';
import { isDriver, useSession } from '../../app/session';
import { ErrorBlock } from '../../components/ErrorBlock';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { useT, type DictKey } from '../../i18n';
import { useFormat } from '../../lib/format';
import { useAsync, useOnVisible } from '../../lib/useAsync';
import { DepartureBlock } from './DepartureWarning';

const ORDER: Record<TripRow['status'], number> = { in_progress: 0, planned: 1, done: 2, cancelled: 3 };

/** Driver: own trips. Office: all trips of the company (toggle «Мої»). Each trip opens its stops and loads. */
export function TripsPage() {
  const t = useT();
  const me = useSession((s) => s.me);
  const driver = isDriver(me);
  const [mine, setMine] = useState(driver);
  const q = useAsync(() => partner.trips(mine ? { driver: 'me' } : undefined), [mine]);
  useOnVisible(() => void q.reload());
  const items = [...(q.data ?? [])].sort((a, b) => ORDER[a.status] - ORDER[b.status] || (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
  return (
    <>
      <PageHeader
        title={t('trips.title')}
        right={
          <Link to="/vehicles" className="btn mt-1">
            {t('trips.vehicles')}
          </Link>
        }
      />
      {!driver ? (
        <div role="tablist" className="mb-4 grid grid-cols-2 gap-2">
          {[false, true].map((v) => (
            <button
              key={String(v)}
              type="button"
              role="tab"
              aria-selected={mine === v}
              onClick={() => setMine(v)}
              className={`min-h-tap rounded-xl border text-[15px] font-medium ${mine === v ? 'border-fg bg-acc text-acc-fg' : 'border-line bg-card'}`}
            >
              {t(v ? 'trips.mine' : 'trips.all')}
            </button>
          ))}
        </div>
      ) : null}
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorBlock error={q.error} onRetry={() => void q.reload()} /> : null}
      {q.data && !items.length ? <p className="text-lab">{t('trips.empty')}</p> : null}
      <ul className="space-y-3">
        {items.map((tr) => (
          <li key={tr.id}>
            <TripCard trip={tr} />
          </li>
        ))}
      </ul>
    </>
  );
}

function TripCard({ trip }: { trip: TripRow }) {
  const t = useT();
  const f = useFormat();
  const [open, setOpen] = useState(false);
  return (
    <div className="card">
      <button type="button" className="tap-row" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[17px] font-semibold">{trip.number ?? '—'}</span>
            <span className="chip">{t(`trips.status.${trip.status}` as DictKey)}</span>
            {trip.handover_id ? <span className="chip">{t('trips.handover')}</span> : null}
          </span>
          <span className="block text-[15px]">{trip.route}</span>
          <span className="block text-[14px] text-lab">
            {f.date(trip.starts_at)} {trip.driver_name ? `· ${trip.driver_name}` : ''}
          </span>
        </span>
      </button>
      {open ? <TripDetail id={trip.id} /> : null}
    </div>
  );
}

function TripDetail({ id }: { id: string }) {
  const t = useT();
  const f = useFormat();
  const q = useAsync(() => partner.trip(id), [id]);
  if (q.loading && !q.data) return <p className="mt-2 text-lab">{t('app.loading')}</p>;
  if (q.error) return <ErrorBlock error={q.error} onRetry={() => void q.reload()} />;
  const d = q.data!;
  const used = Math.max(0, (d.capacity ?? 0) - (d.free_min ?? 0));
  return (
    <div className="mt-3 border-t border-line pt-3 text-[15px]">
      {d.vehicle ? (
        <p className="font-medium">
          {d.vehicle.plate} {d.vehicle.model ? `· ${d.vehicle.model}` : ''}
        </p>
      ) : null}
      {d.capacity ? <p className="text-txt">{t('trips.places', { used, total: d.capacity })}</p> : null}
      <DepartureBlock check={d.departure_check} />
      <h3 className="sec">{t('trips.stops')}</h3>
      <ol className="space-y-1">
        {d.stops.map((s) => (
          <li key={s.id} className="flex gap-2">
            <span className="w-5 flex-none text-lab">{s.seq + 1}.</span>
            <span className="min-w-0 flex-1">
              {s.place}
              {s.country ? `, ${f.country(s.country)}` : ''}
            </span>
            <span className="text-[14px] text-lab">{f.date(s.etd_plan ?? s.eta_plan)}</span>
          </li>
        ))}
      </ol>
      <h3 className="sec">{t('trips.loads')}</h3>
      {d.loads.length ? (
        <ul className="space-y-1">
          {d.loads.map((l) => (
            <li key={l.id}>
              {l.hidden
                ? t('trips.hidden')
                : `${t(l.kind === 'body' ? 'trips.body' : 'trips.cargo')}${l.case_number ? ` · ${l.case_number}` : ''}${l.deceased_name ? ` · ${l.deceased_name}` : ''}`}
              {l.qty > 1 ? ` ×${l.qty}` : ''}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-lab">{t('trips.noLoads')}</p>
      )}
    </div>
  );
}
